import React, { useCallback } from 'react';
import {
	Col,
	Row,
} from 'antd';
import { faExpand } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	Currencies,
	FixtureTypeLabels,
	FixtureTypes,
} from '@shared/utils/constants';
import formatTcAndBbFixtureDuration from '@shared/utils/formatTcAndBbFixtureDuration';
import type { GetFixtureDetailsResponse } from '@api/features/fixtures/getFixtureDetails';
import type { TcFixtureProps } from '@api/models/tc-fixture';
import type { BbFixtureProps } from '@api/models/bb-fixture';
import getEffectivePeriodScheduleItems from '@api/features/fixtures/utils/getEffectivePeriodScheduleItems';
import Details from '@client/components/Details';
import PeriodScheduleTable from '@client/screens/fixtures/tc/PeriodScheduleTable';
import Card from '@client/components/Card/Card';
import Button from '@client/components/Button';
import styles from './styles/SelectItineraryEntryAndDates.module.css';
import ItineraryTab from './ItineraryTab/ItineraryTab';
import { useItinerary } from './ItineraryTab/ItineraryProvider';
import { useVoyage } from './VoyageProvider/VoyageProvider';
import ProjectView from './ProjectView/ProjectView';
import SelectItineraryEntryAndDates from './SelectItineraryEntryAndDates';
import TcPnl from './TcPnl/TcPnl';

const TcSummaryTab = () => {
	const {
		voyageDetails,
		fixtureDetails,
		fixtureCurrency,
		refreshVoyageDetails,
		setShowTcTermsModal,
		refreshDetails,
	} = useVoyage();
	const { refreshItinerary } = useItinerary();

	const handlePeriodScheduleSave = useCallback(async () => {
		await refreshDetails();
		await refreshItinerary();
	}, [refreshDetails, refreshItinerary]);

	if (voyageDetails == null) {
		return null;
	}

	const tcTerms = voyageDetails.tcTerms;

	const effectivePeriodScheduleItems = getEffectivePeriodScheduleItems(
		{ PeriodScheduleItems: fixtureDetails.PeriodScheduleItems },
		tcTerms,
	);

	const tcFixtureDetails =
		fixtureDetails as GetFixtureDetailsResponse<TcFixtureProps | BbFixtureProps>;

	const getOtherItems = () => ([
		{
			type: 'text',
			key: '1',
			label: 'Duration',
			value: formatTcAndBbFixtureDuration({
				items: effectivePeriodScheduleItems,
				laycanFrom: tcTerms?.laycanFrom ?? fixtureDetails.tcTerms?.laycanFrom ?? null,
				laycanTo: tcTerms?.laycanTo ?? fixtureDetails.tcTerms?.laycanTo ?? null,
			}),
		},
		{
			key: '2',
			label: fixtureDetails.type === FixtureTypes.TC_IN ? 'Owner' : 'Charterer',
			value: (
				<>
					{FixtureTypeLabels[fixtureDetails.type]}
					{fixtureDetails.type === FixtureTypes.TC_IN ? ' from ' : ' to '}
					{fixtureDetails.counterpartyName}
				</>
			),
		},
	]);

	if (fixtureDetails.type === FixtureTypes.TC_IN) {
		return (
			<Row gutter={[16, 16]}>
				<Col span={24}>
					<Row gutter={[16, 16]}>
						<Col span={24}>
							<SelectItineraryEntryAndDates
								labelWidth={180}
								columns={4}
							/>
							<Details
								column={4}
								colSpan={2}
								labelWidth={180}
								className={styles.details}
								title={null}
								items={getOtherItems()}
							/>
						</Col>
						<Col span={24}>
							<ProjectView />
						</Col>
					</Row>
				</Col>
			</Row>
		);
	}

	return (
		<Row gutter={[16, 16]}>
			<Col span={18}>
				<Row gutter={[16, 16]}>
					<Col span={24}>
						<SelectItineraryEntryAndDates
							labelWidth={180}
							columns={4}
						/>
					</Col>
					<Col span={24}>
						<Card size="small" title="Period Schedule" extra={(
							<Button
								onClick={() => setShowTcTermsModal(true)}
								icon={(<FontAwesomeIcon icon={faExpand} />)}
							>
								Open TC Terms
							</Button>
						)} slim>
							<PeriodScheduleTable
								refreshFixture={refreshVoyageDetails}
								fixture={tcFixtureDetails}
								periodSchedule={voyageDetails.periodSchedule}
								fixtureCurrency={fixtureCurrency ?? Currencies.USD}
								tcTerms={tcTerms}
								afterSave={handlePeriodScheduleSave}
							/>
						</Card>
					</Col>
					<Col span={24}>
						<ItineraryTab />
					</Col>
				</Row>
			</Col>
			<Col span={6}>
				<TcPnl
					columnsToHide={['invoiced', 'estimated', 'variance']}
					spanOverride={{
						xs: 24,
						sm: 24,
						xl: 24,
						xxl: 24,
					}}
					slim
				/>
			</Col>
		</Row>
	);
};

export default TcSummaryTab;
